import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import { GraduationCapIcon, CalendarIcon, ClockIcon, MapPinIcon, DollarSignIcon, CheckIcon, BookOpenIcon, UsersIcon, BuildingIcon, AwardIcon, ArrowLeftIcon, PencilIcon, ShareIcon, PrinterIcon, DownloadIcon, ChevronRightIcon, TargetIcon, BrainIcon, LightbulbIcon, ZapIcon, SparklesIcon, TagIcon, FileTextIcon, CheckCircleIcon, CopyIcon, ArchiveIcon, ExternalLinkIcon, EyeIcon } from 'lucide-react';
// Sample program data - in a real app, this would come from an API
const samplePrograms = [{
  id: 'PRG001',
  name: 'Master of Business Administration',
  type: "Master's",
  duration: '2 Years',
  status: 'Active',
  applications: 48,
  intakePeriods: ['Fall 2023', 'Spring 2024'],
  description: 'Our MBA program is designed to develop the skills required for careers in business and management. The program integrates theoretical and practical knowledge of business with an international perspective.',
  learningOutcomes: ['Demonstrate advanced knowledge of business concepts and theories', 'Apply strategic thinking to complex business problems', 'Develop leadership skills for diverse business environments', 'Analyze financial data to make informed business decisions', 'Implement effective organizational change strategies', 'Understand global business practices and cross-cultural management'],
  targetAudience: ['Business Professionals', 'Career Changers', 'Recent Graduates', 'Entrepreneurs'],
  deliveryMode: 'Hybrid',
  tuition: '120,000',
  qualifications: ["Bachelor's degree with a minimum GPA of 3.0", '2+ years of professional work experience', 'English proficiency (IELTS 6.5 or equivalent)', 'Two professional references'],
  accreditation: 'Fully accredited by the Ministry of Education and the Association to Advance Collegiate Schools of Business (AACSB)',
  campusLocation: 'Dubai Main Campus',
  skills: [{
    name: 'Strategic Management',
    category: 'Technical',
    confidence: 0.95
  }, {
    name: 'Financial Analysis',
    category: 'Technical',
    confidence: 0.92
  }, {
    name: 'Business Intelligence',
    category: 'Technical',
    confidence: 0.89
  }, {
    name: 'Leadership',
    category: 'Soft',
    confidence: 0.94
  }, {
    name: 'Critical Thinking',
    category: 'Soft',
    confidence: 0.91
  }, {
    name: 'Problem Solving',
    category: 'Soft',
    confidence: 0.9
  }, {
    name: 'Communication',
    category: 'Soft',
    confidence: 0.88
  }, {
    name: 'Business Administration',
    category: 'Industry',
    confidence: 0.93
  }, {
    name: 'Corporate Finance',
    category: 'Industry',
    confidence: 0.87
  }, {
    name: 'Management Consulting',
    category: 'Industry',
    confidence: 0.85
  }],
  enrollmentForm: [{
    label: 'Full Name',
    type: 'text',
    required: true,
    isEligibility: false
  }, {
    label: 'Email Address',
    type: 'email',
    required: true,
    isEligibility: false
  }, {
    label: 'Phone Number',
    type: 'tel',
    required: true,
    isEligibility: false
  }, {
    label: 'Date of Birth',
    type: 'date',
    required: true,
    isEligibility: false
  }, {
    label: 'Nationality',
    type: 'select',
    required: true,
    isEligibility: false
  }, {
    label: 'Current Residence',
    type: 'text',
    required: true,
    isEligibility: false
  }, {
    label: 'Highest Education Level',
    type: 'select',
    required: true,
    isEligibility: true
  }, {
    label: 'Years of Work Experience',
    type: 'number',
    required: true,
    isEligibility: true
  }, {
    label: 'Current Employer',
    type: 'text',
    required: false,
    isEligibility: false
  }, {
    label: 'English Proficiency Test Score',
    type: 'select',
    required: true,
    isEligibility: true
  }, {
    label: 'Personal Statement',
    type: 'textarea',
    required: true,
    isEligibility: false
  }, {
    label: 'CV/Resume',
    type: 'file',
    required: true,
    isEligibility: false
  }, {
    label: 'Academic Transcripts',
    type: 'file',
    required: true,
    isEligibility: true
  }, {
    label: 'Reference Letters',
    type: 'file',
    required: true,
    isEligibility: false
  }],
  documents: [{
    name: 'Program Brochure.pdf',
    type: 'PDF',
    size: '2.4 MB',
    uploaded: '2023-05-15'
  }, {
    name: 'Accreditation Certificate.pdf',
    type: 'PDF',
    size: '1.2 MB',
    uploaded: '2023-04-10'
  }, {
    name: 'Course Catalog.pdf',
    type: 'PDF',
    size: '3.8 MB',
    uploaded: '2023-05-20'
  }],
  facultyMembers: [{
    name: 'Dr. Ahmed Al Mansouri',
    title: 'Program Director',
    image: 'https://randomuser.me/api/portraits/men/1.jpg'
  }, {
    name: 'Dr. Fatima Al Zaabi',
    title: 'Professor of Finance',
    image: 'https://randomuser.me/api/portraits/women/2.jpg'
  }, {
    name: 'Dr. Mohammed Al Hashimi',
    title: 'Professor of Marketing',
    image: 'https://randomuser.me/api/portraits/men/3.jpg'
  }],
  careerOpportunities: ['Business Consultant', 'Financial Analyst', 'Marketing Manager', 'Operations Director', 'Entrepreneur', 'Project Manager'],
  programStructure: [{
    semester: 'Semester 1',
    courses: ['Business Ethics', 'Financial Accounting', 'Marketing Management', 'Organizational Behavior']
  }, {
    semester: 'Semester 2',
    courses: ['Corporate Finance', 'Operations Management', 'Strategic Management', 'Business Analytics']
  }, {
    semester: 'Semester 3',
    courses: ['International Business', 'Leadership and Change', 'Elective 1', 'Elective 2']
  }, {
    semester: 'Semester 4',
    courses: ['Capstone Project', 'Business Consulting Project', 'Elective 3', 'Elective 4']
  }],
  testimonials: [{
    quote: 'The MBA program provided me with both theoretical knowledge and practical skills that were immediately applicable in my career.',
    name: 'Sara Ahmed',
    title: 'Finance Manager, Dubai Holdings',
    image: 'https://randomuser.me/api/portraits/women/65.jpg'
  }, {
    quote: 'The professors bring real-world experience into the classroom, making complex business concepts accessible and relevant.',
    name: 'Omar Al Suwaidi',
    title: 'Senior Consultant, KPMG',
    image: 'https://randomuser.me/api/portraits/men/32.jpg'
  }],
  faqs: [{
    question: 'Can I study this program part-time while working?',
    answer: 'Yes, our MBA program is designed to accommodate working professionals. Classes are scheduled in the evenings and weekends, and the hybrid format allows for some online learning.'
  }, {
    question: 'Are there scholarships available for this program?',
    answer: 'Yes, we offer merit-based scholarships that cover up to 50% of the tuition fees. Additionally, there are specific scholarships for UAE nationals and women in business.'
  }, {
    question: 'What is the application deadline?',
    answer: 'For Fall 2023 intake, the application deadline is June 30, 2023. For Spring 2024, the deadline is November 30, 2023.'
  }]
}, {
  id: 'PRG002',
  name: 'Bachelor of Computer Science',
  type: "Bachelor's",
  duration: '4 Years',
  status: 'Active',
  applications: 112,
  intakePeriods: ['Fall 2023'],
  description: 'The Bachelor of Computer Science program provides students with a comprehensive foundation in computer science principles, programming languages, and software development methodologies. Students will develop technical skills and problem-solving abilities necessary for careers in the rapidly evolving technology sector.',
  learningOutcomes: ['Design and implement efficient algorithms and data structures', 'Develop software using various programming languages and frameworks', 'Apply database design principles and management systems', 'Understand computer networks and security protocols', 'Implement artificial intelligence and machine learning concepts', 'Collaborate effectively in software development teams'],
  targetAudience: ['High School Graduates', 'Technology Enthusiasts', 'Career Changers'],
  deliveryMode: 'In-Person',
  tuition: '80,000',
  qualifications: ['High school diploma with strong performance in mathematics', 'Minimum GPA of 3.0 or equivalent', 'English proficiency (IELTS 6.0 or equivalent)', 'Basic computer literacy'],
  accreditation: 'Accredited by the Ministry of Education and the Computing Accreditation Commission of ABET',
  campusLocation: 'Dubai Main Campus',
  skills: [{
    name: 'Programming',
    category: 'Technical',
    confidence: 0.96
  }, {
    name: 'Data Structures',
    category: 'Technical',
    confidence: 0.94
  }, {
    name: 'Algorithms',
    category: 'Technical',
    confidence: 0.92
  }, {
    name: 'Database Management',
    category: 'Technical',
    confidence: 0.9
  }, {
    name: 'Problem Solving',
    category: 'Soft',
    confidence: 0.95
  }, {
    name: 'Critical Thinking',
    category: 'Soft',
    confidence: 0.93
  }, {
    name: 'Teamwork',
    category: 'Soft',
    confidence: 0.85
  }, {
    name: 'Software Development',
    category: 'Industry',
    confidence: 0.96
  }, {
    name: 'Web Development',
    category: 'Industry',
    confidence: 0.88
  }, {
    name: 'Mobile App Development',
    category: 'Industry',
    confidence: 0.86
  }],
  enrollmentForm: [{
    label: 'Full Name',
    type: 'text',
    required: true,
    isEligibility: false
  }, {
    label: 'Email Address',
    type: 'email',
    required: true,
    isEligibility: false
  }, {
    label: 'Phone Number',
    type: 'tel',
    required: true,
    isEligibility: false
  }, {
    label: 'Date of Birth',
    type: 'date',
    required: true,
    isEligibility: false
  }, {
    label: 'Nationality',
    type: 'select',
    required: true,
    isEligibility: false
  }, {
    label: 'High School Name',
    type: 'text',
    required: true,
    isEligibility: false
  }, {
    label: 'High School GPA',
    type: 'number',
    required: true,
    isEligibility: true
  }, {
    label: 'Mathematics Grade',
    type: 'text',
    required: true,
    isEligibility: true
  }, {
    label: 'English Proficiency Test Score',
    type: 'select',
    required: true,
    isEligibility: true
  }, {
    label: 'Personal Statement',
    type: 'textarea',
    required: true,
    isEligibility: false
  }, {
    label: 'High School Transcripts',
    type: 'file',
    required: true,
    isEligibility: true
  }, {
    label: 'ID/Passport Copy',
    type: 'file',
    required: true,
    isEligibility: false
  }],
  documents: [{
    name: 'Computer Science Brochure.pdf',
    type: 'PDF',
    size: '3.1 MB',
    uploaded: '2023-04-18'
  }, {
    name: 'Course Descriptions.pdf',
    type: 'PDF',
    size: '2.7 MB',
    uploaded: '2023-04-20'
  }, {
    name: 'Lab Facilities Guide.pdf',
    type: 'PDF',
    size: '1.5 MB',
    uploaded: '2023-04-22'
  }],
  facultyMembers: [{
    name: 'Dr. Ali Hassan',
    title: 'Program Director',
    image: 'https://randomuser.me/api/portraits/men/4.jpg'
  }, {
    name: 'Dr. Layla Mahmoud',
    title: 'Professor of AI',
    image: 'https://randomuser.me/api/portraits/women/5.jpg'
  }, {
    name: 'Dr. Khalid Al Mazrouei',
    title: 'Professor of Software Engineering',
    image: 'https://randomuser.me/api/portraits/men/6.jpg'
  }],
  careerOpportunities: ['Software Developer', 'Web Developer', 'Mobile App Developer', 'Database Administrator', 'Systems Analyst', 'IT Consultant'],
  programStructure: [{
    semester: 'Year 1',
    courses: ['Introduction to Programming', 'Discrete Mathematics', 'Computer Systems', 'Calculus', 'Academic English']
  }, {
    semester: 'Year 2',
    courses: ['Data Structures and Algorithms', 'Object-Oriented Programming', 'Database Systems', 'Computer Networks', 'Professional Communication']
  }, {
    semester: 'Year 3',
    courses: ['Software Engineering', 'Operating Systems', 'Web Development', 'Artificial Intelligence', 'Cybersecurity']
  }, {
    semester: 'Year 4',
    courses: ['Capstone Project', 'Machine Learning', 'Cloud Computing', 'Elective 1', 'Elective 2']
  }],
  testimonials: [{
    quote: 'The hands-on approach to learning and industry-relevant curriculum prepared me for a successful career in software development.',
    name: 'Reem Al Falasi',
    title: 'Software Engineer, Microsoft',
    image: 'https://randomuser.me/api/portraits/women/45.jpg'
  }, {
    quote: "The faculty's expertise and mentorship were invaluable in helping me develop both technical skills and professional confidence.",
    name: 'Saif Al Shamsi',
    title: 'Full Stack Developer, Amazon',
    image: 'https://randomuser.me/api/portraits/men/22.jpg'
  }],
  faqs: [{
    question: 'Is prior programming experience required?',
    answer: 'No, the program is designed to accommodate students with no prior programming experience. We start with the fundamentals and progressively build more advanced skills.'
  }, {
    question: 'What kind of computers or equipment do I need?',
    answer: 'Students are required to have a laptop with at least 8GB RAM and an i5 processor or equivalent. The university provides access to specialized software and computing resources.'
  }, {
    question: 'Are there internship opportunities?',
    answer: 'Yes, we have partnerships with leading technology companies in the UAE and internationally. Students typically complete an internship during the summer after their third year.'
  }]
}, {
  id: 'PRG003',
  name: 'Diploma in Digital Marketing',
  type: 'Diploma',
  duration: '1 Year',
  status: 'Draft',
  applications: 0,
  intakePeriods: [],
  description: "This comprehensive diploma program equips students with practical digital marketing skills for today's digital economy. Students will learn to create and execute effective digital marketing strategies across various platforms and channels.",
  learningOutcomes: ['Develop and implement digital marketing strategies', 'Create and manage social media campaigns', 'Utilize SEO and SEM techniques to improve online visibility', 'Analyze marketing data and generate actionable insights', 'Design content marketing plans for various digital platforms', 'Measure and optimize marketing ROI using analytics tools'],
  targetAudience: ['Marketing Professionals', 'Small Business Owners', 'Recent Graduates', 'Career Changers'],
  deliveryMode: 'Online',
  tuition: '35,000',
  qualifications: ['High school diploma or equivalent', 'English proficiency (IELTS 5.5 or equivalent)', 'Basic computer literacy', 'No prior marketing experience required'],
  accreditation: 'Certified by the Digital Marketing Institute (DMI) and recognized by the Ministry of Education',
  campusLocation: 'Online Only',
  skills: [{
    name: 'Digital Marketing Strategy',
    category: 'Technical',
    confidence: 0.93
  }, {
    name: 'Social Media Marketing',
    category: 'Technical',
    confidence: 0.95
  }, {
    name: 'SEO & SEM',
    category: 'Technical',
    confidence: 0.92
  }, {
    name: 'Content Marketing',
    category: 'Technical',
    confidence: 0.9
  }, {
    name: 'Analytics & Data Analysis',
    category: 'Technical',
    confidence: 0.87
  }, {
    name: 'Creativity',
    category: 'Soft',
    confidence: 0.92
  }, {
    name: 'Communication',
    category: 'Soft',
    confidence: 0.91
  }, {
    name: 'Digital Marketing',
    category: 'Industry',
    confidence: 0.97
  }, {
    name: 'Brand Management',
    category: 'Industry',
    confidence: 0.88
  }, {
    name: 'E-commerce',
    category: 'Industry',
    confidence: 0.86
  }],
  enrollmentForm: [{
    label: 'Full Name',
    type: 'text',
    required: true,
    isEligibility: false
  }, {
    label: 'Email Address',
    type: 'email',
    required: true,
    isEligibility: false
  }, {
    label: 'Phone Number',
    type: 'tel',
    required: true,
    isEligibility: false
  }, {
    label: 'Education Level',
    type: 'select',
    required: true,
    isEligibility: true
  }, {
    label: 'Current Occupation',
    type: 'text',
    required: false,
    isEligibility: false
  }, {
    label: 'Computer Proficiency',
    type: 'select',
    required: true,
    isEligibility: true
  }, {
    label: 'Marketing Experience',
    type: 'select',
    required: false,
    isEligibility: false
  }, {
    label: 'Reason for Application',
    type: 'textarea',
    required: true,
    isEligibility: false
  }, {
    label: 'English Proficiency',
    type: 'select',
    required: true,
    isEligibility: true
  }, {
    label: 'ID/Passport Copy',
    type: 'file',
    required: true,
    isEligibility: false
  }],
  documents: [{
    name: 'Digital Marketing Diploma Brochure.pdf',
    type: 'PDF',
    size: '1.8 MB',
    uploaded: '2023-06-01'
  }, {
    name: 'Course Modules Overview.pdf',
    type: 'PDF',
    size: '1.2 MB',
    uploaded: '2023-06-01'
  }],
  facultyMembers: [{
    name: 'Sarah Johnson',
    title: 'Program Coordinator',
    image: 'https://randomuser.me/api/portraits/women/7.jpg'
  }, {
    name: 'Ahmad Al Nasser',
    title: 'Digital Marketing Specialist',
    image: 'https://randomuser.me/api/portraits/men/8.jpg'
  }, {
    name: 'Maryam Al Nuaimi',
    title: 'Social Media Expert',
    image: 'https://randomuser.me/api/portraits/women/9.jpg'
  }],
  careerOpportunities: ['Digital Marketing Specialist', 'Social Media Manager', 'SEO/SEM Specialist', 'Content Marketer', 'Marketing Analyst', 'E-commerce Marketing Coordinator'],
  programStructure: [{
    semester: 'Module 1',
    courses: ['Digital Marketing Fundamentals', 'Social Media Marketing', 'Content Creation & Strategy']
  }, {
    semester: 'Module 2',
    courses: ['Search Engine Optimization', 'Pay-Per-Click Advertising', 'Email Marketing']
  }, {
    semester: 'Module 3',
    courses: ['Analytics & Data Interpretation', 'E-commerce Marketing', 'Mobile Marketing']
  }, {
    semester: 'Module 4',
    courses: ['Digital Marketing Strategy', 'Capstone Project', 'Professional Portfolio Development']
  }],
  testimonials: [],
  faqs: [{
    question: 'Can I complete this program while working full-time?',
    answer: 'Yes, the program is designed to be flexible for working professionals. All courses are available online, and you can access materials and complete assignments at your own pace within the weekly deadlines.'
  }, {
    question: 'Will I receive industry certifications as part of this program?',
    answer: 'Yes, the program prepares you for and includes the cost of certification exams for Google Analytics, Google Ads, and Facebook Blueprint certifications.'
  }, {
    question: 'How is the online learning experience structured?',
    answer: "The program uses a combination of video lectures, interactive workshops, real-world projects, and virtual collaboration tools. You'll have regular access to instructors through virtual office hours and discussion forums."
  }]
}, {
  id: 'PRG004',
  name: 'PhD in Data Science',
  type: 'Doctorate',
  duration: '3 Years',
  status: 'Pending Approval',
  applications: 0,
  intakePeriods: ['Spring 2024'],
  description: 'This doctoral program prepares researchers and professionals to advance the field of data science through original research and innovative applications. Students will develop expertise in advanced statistical methods, machine learning, and data analysis techniques while conducting research that addresses real-world challenges.',
  learningOutcomes: ['Conduct original research in data science and related fields', 'Apply advanced statistical and computational methods to complex problems', 'Develop and evaluate machine learning algorithms and models', 'Design and implement big data solutions for diverse applications', 'Communicate research findings effectively to technical and non-technical audiences', 'Contribute to the theoretical foundations of data science'],
  targetAudience: ['Research-Oriented Professionals', 'MSc Graduates', 'Academic Aspirants'],
  deliveryMode: 'Hybrid',
  tuition: '180,000',
  qualifications: ["Master's degree in Computer Science, Statistics, Mathematics, or related field", 'Minimum GPA of 3.5 in graduate studies', 'Research experience demonstrated through publications or thesis', 'Strong programming skills (Python, R, etc.)', 'English proficiency (IELTS 7.0 or equivalent)'],
  accreditation: 'Pending accreditation by the Ministry of Education and the Computing Research Association',
  campusLocation: 'Dubai Main Campus',
  skills: [{
    name: 'Advanced Statistics',
    category: 'Technical',
    confidence: 0.96
  }, {
    name: 'Machine Learning',
    category: 'Technical',
    confidence: 0.95
  }, {
    name: 'Big Data Technologies',
    category: 'Technical',
    confidence: 0.94
  }, {
    name: 'Research Methodology',
    category: 'Technical',
    confidence: 0.93
  }, {
    name: 'Critical Thinking',
    category: 'Soft',
    confidence: 0.97
  }, {
    name: 'Problem Solving',
    category: 'Soft',
    confidence: 0.96
  }, {
    name: 'Academic Writing',
    category: 'Soft',
    confidence: 0.92
  }, {
    name: 'Data Science',
    category: 'Industry',
    confidence: 0.98
  }, {
    name: 'Artificial Intelligence',
    category: 'Industry',
    confidence: 0.94
  }, {
    name: 'Research & Development',
    category: 'Industry',
    confidence: 0.93
  }],
  enrollmentForm: [{
    label: 'Full Name',
    type: 'text',
    required: true,
    isEligibility: false
  }, {
    label: 'Email Address',
    type: 'email',
    required: true,
    isEligibility: false
  }, {
    label: 'Phone Number',
    type: 'tel',
    required: true,
    isEligibility: false
  }, {
    label: 'Masters Degree',
    type: 'select',
    required: true,
    isEligibility: true
  }, {
    label: 'Masters GPA',
    type: 'number',
    required: true,
    isEligibility: true
  }, {
    label: 'Research Experience',
    type: 'textarea',
    required: true,
    isEligibility: true
  }, {
    label: 'Programming Skills',
    type: 'select',
    required: true,
    isEligibility: true
  }, {
    label: 'Research Proposal',
    type: 'textarea',
    required: true,
    isEligibility: false
  }, {
    label: 'Publication List',
    type: 'textarea',
    required: false,
    isEligibility: false
  }, {
    label: 'English Proficiency Test Score',
    type: 'select',
    required: true,
    isEligibility: true
  }, {
    label: 'CV/Resume',
    type: 'file',
    required: true,
    isEligibility: false
  }, {
    label: 'Academic Transcripts',
    type: 'file',
    required: true,
    isEligibility: true
  }, {
    label: 'Research Sample',
    type: 'file',
    required: true,
    isEligibility: true
  }, {
    label: 'Reference Letters',
    type: 'file',
    required: true,
    isEligibility: false
  }],
  documents: [{
    name: 'PhD Program Overview.pdf',
    type: 'PDF',
    size: '2.5 MB',
    uploaded: '2023-05-25'
  }, {
    name: 'Research Areas Guide.pdf',
    type: 'PDF',
    size: '1.8 MB',
    uploaded: '2023-05-25'
  }, {
    name: 'Faculty Research Interests.pdf',
    type: 'PDF',
    size: '1.2 MB',
    uploaded: '2023-05-25'
  }],
  facultyMembers: [{
    name: 'Prof. Jamal Al Qasimi',
    title: 'Program Director',
    image: 'https://randomuser.me/api/portraits/men/10.jpg'
  }, {
    name: 'Dr. Leila Tabrizi',
    title: 'Professor of Machine Learning',
    image: 'https://randomuser.me/api/portraits/women/11.jpg'
  }, {
    name: 'Dr. Tariq Rahman',
    title: 'Professor of Computational Statistics',
    image: 'https://randomuser.me/api/portraits/men/12.jpg'
  }],
  careerOpportunities: ['Research Scientist', 'Data Science Director', 'AI Research Lead', 'University Professor', 'Chief Data Officer', 'Advanced Analytics Consultant'],
  programStructure: [{
    semester: 'Year 1',
    courses: ['Advanced Machine Learning', 'Statistical Computing', 'Research Methods in Data Science', 'Big Data Systems', 'Elective']
  }, {
    semester: 'Year 2',
    courses: ['Dissertation Research', 'Teaching Assistantship', 'Research Seminar']
  }, {
    semester: 'Year 3',
    courses: ['Dissertation Research', 'Dissertation Defense']
  }],
  testimonials: [],
  faqs: [{
    question: 'Is full-time enrollment required?',
    answer: 'Yes, this is primarily a full-time program, though limited part-time options may be available for exceptional candidates with significant research experience.'
  }, {
    question: 'What funding opportunities are available?',
    answer: 'We offer a limited number of research assistantships and teaching fellowships that provide tuition waivers and stipends. External funding and industry sponsorships are also options for qualified candidates.'
  }, {
    question: 'What research facilities are available?',
    answer: 'Our Data Science Research Center features high-performance computing clusters, specialized laboratories for AI and machine learning, and partnerships with industry for access to real-world datasets and problems.'
  }]
}, {
  id: 'PRG005',
  name: 'Master of Public Administration',
  type: "Master's",
  duration: '2 Years',
  status: 'Active',
  applications: 27,
  intakePeriods: ['Fall 2023', 'Spring 2024'],
  description: 'The Master of Public Administration program prepares students for leadership roles in government, non-profit organizations, and public service. The curriculum focuses on policy analysis, public management, and governance in the context of the UAE and global best practices.',
  learningOutcomes: ['Analyze public policy issues and develop effective solutions', 'Apply principles of effective public administration and management', 'Understand government structures and decision-making processes', 'Develop and manage public budgets and financial resources', 'Design and evaluate public programs and services', 'Lead diverse teams in public and non-profit organizations'],
  targetAudience: ['Government Employees', 'Non-profit Professionals', 'Policy Analysts', 'Recent Graduates'],
  deliveryMode: 'In-Person',
  tuition: '110,000',
  qualifications: ["Bachelor's degree in any field with minimum GPA of 3.0", 'Professional experience in public sector preferred but not required', 'English proficiency (IELTS 6.5 or equivalent)', 'Statement of purpose and two letters of recommendation'],
  accreditation: 'Accredited by the Ministry of Education and the Network of Schools of Public Policy, Affairs, and Administration (NASPAA)',
  campusLocation: 'Abu Dhabi Campus',
  skills: [{
    name: 'Policy Analysis',
    category: 'Technical',
    confidence: 0.94
  }, {
    name: 'Public Management',
    category: 'Technical',
    confidence: 0.93
  }, {
    name: 'Budgeting & Finance',
    category: 'Technical',
    confidence: 0.91
  }, {
    name: 'Program Evaluation',
    category: 'Technical',
    confidence: 0.89
  }, {
    name: 'Leadership',
    category: 'Soft',
    confidence: 0.95
  }, {
    name: 'Critical Thinking',
    category: 'Soft',
    confidence: 0.93
  }, {
    name: 'Communication',
    category: 'Soft',
    confidence: 0.92
  }, {
    name: 'Public Administration',
    category: 'Industry',
    confidence: 0.96
  }, {
    name: 'Government Relations',
    category: 'Industry',
    confidence: 0.92
  }, {
    name: 'Non-profit Management',
    category: 'Industry',
    confidence: 0.9
  }],
  enrollmentForm: [{
    label: 'Full Name',
    type: 'text',
    required: true,
    isEligibility: false
  }, {
    label: 'Email Address',
    type: 'email',
    required: true,
    isEligibility: false
  }, {
    label: 'Phone Number',
    type: 'tel',
    required: true,
    isEligibility: false
  }, {
    label: 'Date of Birth',
    type: 'date',
    required: true,
    isEligibility: false
  }, {
    label: 'Nationality',
    type: 'select',
    required: true,
    isEligibility: false
  }, {
    label: 'Current Employer',
    type: 'text',
    required: false,
    isEligibility: false
  }, {
    label: 'Job Title',
    type: 'text',
    required: false,
    isEligibility: false
  }, {
    label: 'Years of Professional Experience',
    type: 'number',
    required: true,
    isEligibility: false
  }, {
    label: 'Undergraduate Degree',
    type: 'text',
    required: true,
    isEligibility: true
  }, {
    label: 'Undergraduate GPA',
    type: 'number',
    required: true,
    isEligibility: true
  }, {
    label: 'English Proficiency Test Score',
    type: 'select',
    required: true,
    isEligibility: true
  }, {
    label: 'Statement of Purpose',
    type: 'textarea',
    required: true,
    isEligibility: false
  }, {
    label: 'CV/Resume',
    type: 'file',
    required: true,
    isEligibility: false
  }, {
    label: 'Academic Transcripts',
    type: 'file',
    required: true,
    isEligibility: true
  }, {
    label: 'Reference Letters',
    type: 'file',
    required: true,
    isEligibility: false
  }],
  documents: [{
    name: 'MPA Program Brochure.pdf',
    type: 'PDF',
    size: '2.2 MB',
    uploaded: '2023-04-05'
  }, {
    name: 'NASPAA Accreditation Certificate.pdf',
    type: 'PDF',
    size: '1.1 MB',
    uploaded: '2023-04-05'
  }, {
    name: 'Public Sector Career Guide.pdf',
    type: 'PDF',
    size: '1.8 MB',
    uploaded: '2023-04-10'
  }],
  facultyMembers: [{
    name: 'Dr. Hessa Al Otaiba',
    title: 'Program Director',
    image: 'https://randomuser.me/api/portraits/women/13.jpg'
  }, {
    name: 'Dr. Nasser Al Dhaheri',
    title: 'Professor of Public Policy',
    image: 'https://randomuser.me/api/portraits/men/14.jpg'
  }, {
    name: 'Dr. Mariam Al Mheiri',
    title: 'Professor of Governance',
    image: 'https://randomuser.me/api/portraits/women/15.jpg'
  }],
  careerOpportunities: ['Government Administrator', 'Policy Analyst', 'Program Manager', 'Non-profit Director', 'Public Affairs Consultant', 'International Development Specialist'],
  programStructure: [{
    semester: 'Semester 1',
    courses: ['Foundations of Public Administration', 'Public Policy Analysis', 'Research Methods', 'UAE Government and Politics']
  }, {
    semester: 'Semester 2',
    courses: ['Public Financial Management', 'Human Resources in the Public Sector', 'Ethics in Public Service', 'E-Government']
  }, {
    semester: 'Semester 3',
    courses: ['Strategic Planning', 'Program Evaluation', 'Leadership in Public Organizations', 'Elective']
  }, {
    semester: 'Semester 4',
    courses: ['Capstone Project', 'Public-Private Partnerships', 'Elective', 'Elective']
  }],
  testimonials: [{
    quote: 'The MPA program provided me with the knowledge and skills to make a meaningful impact in government service.',
    name: 'Abdulrahman Al Awar',
    title: 'Director, Ministry of Human Resources',
    image: 'https://randomuser.me/api/portraits/men/55.jpg'
  }, {
    quote: 'The blend of theory and practical application prepared me for the complex challenges of public sector leadership.',
    name: 'Noura Al Kaabi',
    title: 'Executive Director, Abu Dhabi Foundation',
    image: 'https://randomuser.me/api/portraits/women/62.jpg'
  }],
  faqs: [{
    question: 'Is work experience required for admission?',
    answer: 'While professional experience in the public sector is preferred, it is not mandatory. Recent graduates with strong academic backgrounds and a demonstrated interest in public service are encouraged to apply.'
  }, {
    question: 'Are there opportunities for international exposure?',
    answer: 'Yes, the program includes an optional international study tour to examine public administration practices in other countries. We also have exchange partnerships with leading public policy schools globally.'
  }, {
    question: 'How is the program connected to government entities?',
    answer: 'We maintain strong partnerships with federal and local government agencies in the UAE. Many courses feature guest speakers from government, and students have opportunities for internships and capstone projects with government entities.'
  }]
}, {
  id: 'PRG006',
  name: 'Bachelor of Business Administration',
  type: "Bachelor's",
  duration: '4 Years',
  status: 'Archived',
  applications: 0,
  intakePeriods: [],
  description: 'The Bachelor of Business Administration program provides a comprehensive foundation in business principles and practices. Students develop skills in management, marketing, finance, and operations to prepare for careers across various industries.',
  learningOutcomes: ['Apply fundamental business concepts and theories to real-world scenarios', 'Develop effective management and leadership skills', 'Analyze financial data and make informed business decisions', 'Understand marketing principles and consumer behavior', 'Implement operational strategies to improve organizational efficiency', 'Demonstrate ethical business practices and social responsibility'],
  targetAudience: ['High School Graduates', 'Working Professionals', 'Entrepreneurs'],
  deliveryMode: 'In-Person',
  tuition: '75,000',
  qualifications: ['High school diploma with minimum GPA of 2.5', 'English proficiency (IELTS 5.5 or equivalent)', 'Basic mathematics proficiency', 'Personal statement'],
  accreditation: 'Accredited by the Ministry of Education and the Association to Advance Collegiate Schools of Business (AACSB)',
  campusLocation: 'Dubai Main Campus',
  skills: [{
    name: 'Business Management',
    category: 'Technical',
    confidence: 0.92
  }, {
    name: 'Financial Analysis',
    category: 'Technical',
    confidence: 0.88
  }, {
    name: 'Marketing',
    category: 'Technical',
    confidence: 0.9
  }, {
    name: 'Operations Management',
    category: 'Technical',
    confidence: 0.87
  }, {
    name: 'Communication',
    category: 'Soft',
    confidence: 0.93
  }, {
    name: 'Leadership',
    category: 'Soft',
    confidence: 0.91
  }, {
    name: 'Problem Solving',
    category: 'Soft',
    confidence: 0.89
  }, {
    name: 'Business Administration',
    category: 'Industry',
    confidence: 0.95
  }, {
    name: 'Entrepreneurship',
    category: 'Industry',
    confidence: 0.88
  }, {
    name: 'Retail Management',
    category: 'Industry',
    confidence: 0.86
  }],
  enrollmentForm: [{
    label: 'Full Name',
    type: 'text',
    required: true,
    isEligibility: false
  }, {
    label: 'Email Address',
    type: 'email',
    required: true,
    isEligibility: false
  }, {
    label: 'Phone Number',
    type: 'tel',
    required: true,
    isEligibility: false
  }, {
    label: 'Date of Birth',
    type: 'date',
    required: true,
    isEligibility: false
  }, {
    label: 'Nationality',
    type: 'select',
    required: true,
    isEligibility: false
  }, {
    label: 'High School Name',
    type: 'text',
    required: true,
    isEligibility: false
  }, {
    label: 'High School GPA',
    type: 'number',
    required: true,
    isEligibility: true
  }, {
    label: 'Mathematics Grade',
    type: 'text',
    required: true,
    isEligibility: true
  }, {
    label: 'English Proficiency Test Score',
    type: 'select',
    required: true,
    isEligibility: true
  }, {
    label: 'Personal Statement',
    type: 'textarea',
    required: true,
    isEligibility: false
  }, {
    label: 'High School Transcripts',
    type: 'file',
    required: true,
    isEligibility: true
  }, {
    label: 'ID/Passport Copy',
    type: 'file',
    required: true,
    isEligibility: false
  }],
  documents: [{
    name: 'BBA Program Brochure.pdf',
    type: 'PDF',
    size: '2.8 MB',
    uploaded: '2022-05-10'
  }, {
    name: 'Course Catalog.pdf',
    type: 'PDF',
    size: '3.2 MB',
    uploaded: '2022-05-10'
  }, {
    name: 'Career Pathways Guide.pdf',
    type: 'PDF',
    size: '1.5 MB',
    uploaded: '2022-05-15'
  }],
  facultyMembers: [{
    name: 'Dr. Khalid Al Mulla',
    title: 'Former Program Director',
    image: 'https://randomuser.me/api/portraits/men/16.jpg'
  }, {
    name: 'Dr. Aisha Al Hammadi',
    title: 'Professor of Marketing',
    image: 'https://randomuser.me/api/portraits/women/17.jpg'
  }, {
    name: 'Dr. Saeed Al Mansoori',
    title: 'Professor of Finance',
    image: 'https://randomuser.me/api/portraits/men/18.jpg'
  }],
  careerOpportunities: ['Business Manager', 'Marketing Coordinator', 'Financial Analyst', 'Human Resources Specialist', 'Operations Coordinator', 'Entrepreneur'],
  programStructure: [{
    semester: 'Year 1',
    courses: ['Introduction to Business', 'Principles of Economics', 'Business Mathematics', 'Communication Skills', 'UAE Business Environment']
  }, {
    semester: 'Year 2',
    courses: ['Principles of Management', 'Principles of Marketing', 'Financial Accounting', 'Business Statistics', 'Business Law']
  }, {
    semester: 'Year 3',
    courses: ['Organizational Behavior', 'Marketing Research', 'Corporate Finance', 'Operations Management', 'Management Information Systems']
  }, {
    semester: 'Year 4',
    courses: ['Strategic Management', 'Business Ethics', 'Entrepreneurship', 'Capstone Project', 'Electives']
  }],
  testimonials: [{
    quote: 'The BBA program provided me with a solid foundation in business that has been invaluable throughout my career.',
    name: 'Mohammed Al Shamsi',
    title: 'Operations Manager, Emirates Group',
    image: 'https://randomuser.me/api/portraits/men/75.jpg'
  }, {
    quote: 'The practical approach to learning and internship opportunities helped me secure a great job immediately after graduation.',
    name: 'Latifa Al Maktoum',
    title: 'Marketing Specialist, Etisalat',
    image: 'https://randomuser.me/api/portraits/women/72.jpg'
  }],
  faqs: [{
    question: 'Why is this program archived?',
    answer: 'This program has been replaced by our new Bachelor of Business Administration with specializations. Current students will complete their degrees, but no new enrollments are being accepted.'
  }, {
    question: 'What is the difference between this program and the new BBA?',
    answer: 'The new BBA program offers specialized tracks in Digital Business, Entrepreneurship, and Sustainable Business Management, reflecting current industry trends and employer needs.'
  }, {
    question: "I'm interested in business studies. What should I apply for?",
    answer: 'We recommend applying for our new Bachelor of Business Administration with specializations program, which builds on the strengths of this program while adding new cutting-edge content and specialization options.'
  }]
}];
// Skill badge component
const SkillBadge = ({
  name,
  category,
  confidence
}) => {
  const getCategoryColor = () => {
    switch (category) {
      case 'Technical':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'Soft':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'Industry':
        return 'bg-green-100 text-green-800 border-green-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };
  const getConfidenceIcon = () => {
    if (confidence >= 0.9) {
      return <CheckIcon className="w-3 h-3 text-green-600" data-id="element-6096" />;
    }
    return null;
  };
  return <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm border ${getCategoryColor()}`} title={`Confidence: ${Math.round(confidence * 100)}%`} data-id="element-6097">
      {name}
      {confidence >= 0.9 && <span className="ml-1.5 flex-shrink-0" data-id="element-6098">{getConfidenceIcon()}</span>}
    </span>;
};
// FAQ Accordion Item
const FAQItem = ({
  question,
  answer
}) => {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="border-b border-gray-200 last:border-b-0" data-id="element-6099">
      <button className="flex justify-between items-center w-full py-4 text-left font-medium text-gray-900 hover:text-primary-600 focus:outline-none" onClick={() => setIsOpen(!isOpen)} data-id="element-6100">
        <span data-id="element-6101">{question}</span>
        <ChevronRightIcon className={`w-5 h-5 text-gray-500 transition-transform ${isOpen ? 'transform rotate-90' : ''}`} data-id="element-6102" />
      </button>
      <div className={`overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-96 pb-4' : 'max-h-0'}`} data-id="element-6103">
        <p className="text-gray-600" data-id="element-6104">{answer}</p>
      </div>
    </div>;
};
const ProgramDetail = () => {
  const {
    programId
  } = useParams();
  const navigate = useNavigate();
  const [program, setProgram] = useState(null);
  const [loading, setLoading] = useState(true);
  const [headerVisible, setHeaderVisible] = useState(false);
  useEffect(() => {
    // In a real app, this would be an API call
    const fetchProgram = () => {
      setLoading(true);
      // Find the program with the matching ID
      const foundProgram = samplePrograms.find(p => p.id === programId);
      if (foundProgram) {
        setProgram(foundProgram);
      } else {
        // Handle program not found
        console.error('Program not found');
      }
      setLoading(false);
    };
    fetchProgram();
    // Add scroll listener for sticky header
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setHeaderVisible(scrollPosition > 300);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [programId]);
  if (loading) {
    return <Layout title="Program Details" persona="University Coordinator" universityName="UAE Business School" data-id="element-6105">
        <div className="flex justify-center items-center h-64" data-id="element-6106">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500" data-id="element-6107"></div>
        </div>
      </Layout>;
  }
  if (!program) {
    return <Layout title="Program Not Found" persona="University Coordinator" universityName="UAE Business School" data-id="element-6108">
        <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm text-center" data-id="element-6109">
          <div className="mx-auto w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4" data-id="element-6110">
            <GraduationCapIcon className="w-8 h-8 text-gray-400" data-id="element-6111" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2" data-id="element-6112">
            Program Not Found
          </h2>
          <p className="text-gray-600 mb-6" data-id="element-6113">
            The program you're looking for doesn't exist or has been removed.
          </p>
          <button onClick={() => navigate('/university/programs')} className="bg-primary-500 text-white px-4 py-2 rounded-md hover:bg-primary-600 transition-colors" data-id="element-6114">
            Back to Programs
          </button>
        </div>
      </Layout>;
  }
  // Helper function to get status color
  const getStatusColor = status => {
    switch (status) {
      case 'Active':
        return 'bg-green-100 text-green-800';
      case 'Draft':
        return 'bg-gray-100 text-gray-800';
      case 'Pending Approval':
        return 'bg-yellow-100 text-yellow-800';
      case 'Archived':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-blue-100 text-blue-800';
    }
  };
  return <Layout title={program.name} persona="University Coordinator" universityName="UAE Business School" data-id="element-6115">
      {/* Sticky header - appears on scroll */}
      {headerVisible && <div className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-200 shadow-sm transform transition-all duration-300" style={{
      left: '16rem'
    }} data-id="element-6116">
          <div className="max-w-7xl mx-auto px-6 py-3 flex justify-between items-center" data-id="element-6117">
            <div className="flex items-center" data-id="element-6118">
              <h2 className="text-lg font-semibold text-gray-800 truncate mr-4" data-id="element-6119">
                {program.name}
              </h2>
              <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${getStatusColor(program.status)}`} data-id="element-6120">
                {program.status}
              </span>
            </div>
            <div className="flex space-x-2" data-id="element-6121">
              <button className="bg-primary-500 text-white px-3 py-1.5 rounded-md text-sm font-medium hover:bg-primary-600 transition-colors flex items-center" data-id="element-6122">
                <PencilIcon className="w-4 h-4 mr-1" data-id="element-6123" />
                Edit
              </button>
              <button className="bg-white text-gray-700 border border-gray-300 px-3 py-1.5 rounded-md text-sm font-medium hover:bg-gray-50 transition-colors" onClick={() => navigate('/university/programs')} data-id="element-6124">
                Back to Programs
              </button>
            </div>
          </div>
        </div>}
      {/* Back button */}
      <button onClick={() => navigate('/university/programs')} className="mb-6 flex items-center text-sm text-gray-600 hover:text-gray-800" data-id="element-6125">
        <ArrowLeftIcon className="w-4 h-4 mr-1" data-id="element-6126" />
        Back to Programs
      </button>
      {/* Program Header */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden mb-8" data-id="element-6127">
        <div className="bg-gradient-to-r from-primary-500 to-primary-600 px-8 py-12 text-white" data-id="element-6128">
          <div className="max-w-5xl mx-auto" data-id="element-6129">
            <div className="flex flex-wrap items-center justify-between" data-id="element-6130">
              <div className="w-full lg:w-2/3 mb-6 lg:mb-0" data-id="element-6131">
                <div className="flex items-center mb-4" data-id="element-6132">
                  <span className={`px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(program.status)}`} data-id="element-6133">
                    {program.status}
                  </span>
                  <span className="mx-2 text-white/70" data-id="element-6134">•</span>
                  <span className="text-white/90" data-id="element-6135">{program.type}</span>
                  <span className="mx-2 text-white/70" data-id="element-6136">•</span>
                  <span className="text-white/90" data-id="element-6137">{program.duration}</span>
                </div>
                <h1 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-6138">
                  {program.name}
                </h1>
                <p className="text-lg text-white/90 mb-6" data-id="element-6139">
                  {program.description}
                </p>
                <div className="flex flex-wrap gap-3" data-id="element-6140">
                  {program.intakePeriods.length > 0 ? program.intakePeriods.map((period, index) => <span key={index} className="inline-flex items-center px-3 py-1 bg-white/20 rounded-full text-sm" data-id="element-6141">
                        <CalendarIcon className="w-4 h-4 mr-1" data-id="element-6142" />
                        {period} Intake
                      </span>) : <span className="inline-flex items-center px-3 py-1 bg-white/20 rounded-full text-sm" data-id="element-6143">
                      <CalendarIcon className="w-4 h-4 mr-1" data-id="element-6144" />
                      No active intakes
                    </span>}
                  <span className="inline-flex items-center px-3 py-1 bg-white/20 rounded-full text-sm" data-id="element-6145">
                    <BuildingIcon className="w-4 h-4 mr-1" data-id="element-6146" />
                    {program.campusLocation}
                  </span>
                  <span className="inline-flex items-center px-3 py-1 bg-white/20 rounded-full text-sm" data-id="element-6147">
                    <BookOpenIcon className="w-4 h-4 mr-1" data-id="element-6148" />
                    {program.deliveryMode}
                  </span>
                </div>
              </div>
              <div className="w-full lg:w-1/3 lg:pl-8" data-id="element-6149">
                <div className="bg-white rounded-lg p-6 text-gray-900 shadow-lg" data-id="element-6150">
                  <div className="flex justify-between items-center mb-4" data-id="element-6151">
                    <h3 className="font-semibold text-lg" data-id="element-6152">Program Actions</h3>
                    <GraduationCapIcon className="w-6 h-6 text-primary-500" data-id="element-6153" />
                  </div>
                  <div className="space-y-4" data-id="element-6154">
                    <button className="w-full bg-primary-500 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-primary-600 transition-colors flex items-center justify-center" data-id="element-6155">
                      <PencilIcon className="w-4 h-4 mr-2" data-id="element-6156" />
                      Edit Program
                    </button>
                    <button className="w-full bg-white text-gray-700 border border-gray-300 px-4 py-2 rounded-md text-sm font-medium hover:bg-gray-50 transition-colors flex items-center justify-center" data-id="element-6157">
                      <CopyIcon className="w-4 h-4 mr-2" data-id="element-6158" />
                      Duplicate Program
                    </button>
                    <button className="w-full bg-white text-red-600 border border-red-200 px-4 py-2 rounded-md text-sm font-medium hover:bg-red-50 transition-colors flex items-center justify-center" data-id="element-6159">
                      <ArchiveIcon className="w-4 h-4 mr-2" data-id="element-6160" />
                      {program.status === 'Archived' ? 'Restore Program' : 'Archive Program'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Quick navigation */}
        <div className="bg-gray-50 border-b border-gray-200 px-8 py-4 overflow-x-auto" data-id="element-6161">
          <div className="max-w-5xl mx-auto" data-id="element-6162">
            <div className="flex space-x-6" data-id="element-6163">
              <a href="#overview" className="text-sm font-medium text-gray-700 hover:text-primary-500 whitespace-nowrap" data-id="element-6164">
                Overview
              </a>
              <a href="#learning-outcomes" className="text-sm font-medium text-gray-700 hover:text-primary-500 whitespace-nowrap" data-id="element-6165">
                Learning Outcomes
              </a>
              <a href="#admission-requirements" className="text-sm font-medium text-gray-700 hover:text-primary-500 whitespace-nowrap" data-id="element-6166">
                Admission Requirements
              </a>
              <a href="#skills" className="text-sm font-medium text-gray-700 hover:text-primary-500 whitespace-nowrap" data-id="element-6167">
                Skills & Competencies
              </a>
              <a href="#enrollment-form" className="text-sm font-medium text-gray-700 hover:text-primary-500 whitespace-nowrap" data-id="element-6168">
                Enrollment Form
              </a>
              <a href="#documents" className="text-sm font-medium text-gray-700 hover:text-primary-500 whitespace-nowrap" data-id="element-6169">
                Documents
              </a>
              <a href="#structure" className="text-sm font-medium text-gray-700 hover:text-primary-500 whitespace-nowrap" data-id="element-6170">
                Program Structure
              </a>
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-5xl mx-auto" data-id="element-6171">
        {/* Main content */}
        <div className="space-y-8" data-id="element-6172">
          {/* Overview Section */}
          <section id="overview" className="bg-white rounded-lg border border-gray-200 shadow-sm p-8" data-id="element-6173">
            <h2 className="text-2xl font-bold text-gray-800 mb-6" data-id="element-6174">
              Program Overview
            </h2>
            <p className="text-gray-700 mb-6" data-id="element-6175">{program.description}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-6176">
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-100" data-id="element-6177">
                <div className="flex items-center mb-3" data-id="element-6178">
                  <BookOpenIcon className="w-5 h-5 text-primary-500 mr-2" data-id="element-6179" />
                  <h3 className="font-medium text-gray-800" data-id="element-6180">Delivery Mode</h3>
                </div>
                <p className="text-gray-600" data-id="element-6181">{program.deliveryMode}</p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-100" data-id="element-6182">
                <div className="flex items-center mb-3" data-id="element-6183">
                  <ClockIcon className="w-5 h-5 text-primary-500 mr-2" data-id="element-6184" />
                  <h3 className="font-medium text-gray-800" data-id="element-6185">Duration</h3>
                </div>
                <p className="text-gray-600" data-id="element-6186">{program.duration}</p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-100" data-id="element-6187">
                <div className="flex items-center mb-3" data-id="element-6188">
                  <AwardIcon className="w-5 h-5 text-primary-500 mr-2" data-id="element-6189" />
                  <h3 className="font-medium text-gray-800" data-id="element-6190">Accreditation</h3>
                </div>
                <p className="text-gray-600" data-id="element-6191">{program.accreditation}</p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-100" data-id="element-6192">
                <div className="flex items-center mb-3" data-id="element-6193">
                  <MapPinIcon className="w-5 h-5 text-primary-500 mr-2" data-id="element-6194" />
                  <h3 className="font-medium text-gray-800" data-id="element-6195">Campus Location</h3>
                </div>
                <p className="text-gray-600" data-id="element-6196">{program.campusLocation}</p>
              </div>
            </div>
            {/* Target Audience */}
            {program.targetAudience && program.targetAudience.length > 0 && <div data-id="element-6197">
                <h3 className="text-lg font-semibold text-gray-800 mb-3" data-id="element-6198">
                  Target Audience
                </h3>
                <div className="flex flex-wrap gap-2 mb-6" data-id="element-6199">
                  {program.targetAudience.map((audience, index) => <span key={index} className="bg-primary-50 text-primary-700 px-3 py-1 rounded-full text-sm" data-id="element-6200">
                      {audience}
                    </span>)}
                </div>
              </div>}
            {/* Tuition & Fees */}
            <div data-id="element-6201">
              <h3 className="text-lg font-semibold text-gray-800 mb-3" data-id="element-6202">
                Tuition & Fees
              </h3>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-100 mb-4" data-id="element-6203">
                <div className="flex items-center justify-between" data-id="element-6204">
                  <div data-id="element-6205">
                    <p className="text-gray-600 text-sm" data-id="element-6206">Total Program Cost</p>
                    <p className="text-xl font-semibold text-gray-900" data-id="element-6207">
                      AED {program.tuition}
                    </p>
                  </div>
                </div>
              </div>
              <p className="text-sm text-gray-500" data-id="element-6208">
                * Additional fees may apply for textbooks, materials, and
                student services.
              </p>
            </div>
          </section>
          {/* Learning Outcomes */}
          <section id="learning-outcomes" className="bg-white rounded-lg border border-gray-200 shadow-sm p-8" data-id="element-6209">
            <h2 className="text-2xl font-bold text-gray-800 mb-6" data-id="element-6210">
              Learning Outcomes
            </h2>
            <p className="text-gray-700 mb-6" data-id="element-6211">
              Upon successful completion of this program, students will be able
              to:
            </p>
            <div className="space-y-4" data-id="element-6212">
              {program.learningOutcomes.map((outcome, index) => <div key={index} className="flex items-start" data-id="element-6213">
                  <div className="flex-shrink-0 mt-1" data-id="element-6214">
                    <div className="w-6 h-6 flex items-center justify-center text-primary-600" data-id="element-6215">
                      <CheckIcon className="w-4 h-4" data-id="element-6216" />
                    </div>
                  </div>
                  <p className="ml-4 text-gray-700" data-id="element-6217">{outcome}</p>
                </div>)}
            </div>
          </section>
          {/* Admission Requirements */}
          <section id="admission-requirements" className="bg-white rounded-lg border border-gray-200 shadow-sm p-8" data-id="element-6218">
            <h2 className="text-2xl font-bold text-gray-800 mb-6" data-id="element-6219">
              Admission Requirements
            </h2>
            <p className="text-gray-700 mb-6" data-id="element-6220">
              Applicants to this program must meet the following requirements:
            </p>
            <div className="space-y-4" data-id="element-6221">
              {program.qualifications.map((qualification, index) => <div key={index} className="flex items-start" data-id="element-6222">
                  <div className="flex-shrink-0 mt-1" data-id="element-6223">
                    <div className="w-6 h-6 flex items-center justify-center text-primary-600" data-id="element-6224">
                      <CheckIcon className="w-4 h-4" data-id="element-6225" />
                    </div>
                  </div>
                  <p className="ml-4 text-gray-700" data-id="element-6226">{qualification}</p>
                </div>)}
            </div>
          </section>
          {/* Skills & Competencies */}
          <section id="skills" className="bg-white rounded-lg border border-gray-200 shadow-sm p-8" data-id="element-6227">
            <h2 className="text-2xl font-bold text-gray-800 mb-6" data-id="element-6228">
              Skills & Competencies
            </h2>
            <p className="text-gray-700 mb-6" data-id="element-6229">
              Students will develop the following skills through this program:
            </p>
            <div className="space-y-6" data-id="element-6230">
              {/* Technical Skills */}
              <div data-id="element-6231">
                <div className="flex items-center mb-3" data-id="element-6232">
                  <BrainIcon className="w-5 h-5 text-blue-600 mr-2" data-id="element-6233" />
                  <h3 className="font-medium text-gray-800" data-id="element-6234">
                    Technical Skills
                  </h3>
                </div>
                <div className="flex flex-wrap gap-2" data-id="element-6235">
                  {program.skills.filter(skill => skill.category === 'Technical').map((skill, index) => <SkillBadge key={index} {...skill} data-id="element-6236" />)}
                </div>
              </div>
              {/* Soft Skills */}
              <div data-id="element-6237">
                <div className="flex items-center mb-3" data-id="element-6238">
                  <LightbulbIcon className="w-5 h-5 text-purple-600 mr-2" data-id="element-6239" />
                  <h3 className="font-medium text-gray-800" data-id="element-6240">Soft Skills</h3>
                </div>
                <div className="flex flex-wrap gap-2" data-id="element-6241">
                  {program.skills.filter(skill => skill.category === 'Soft').map((skill, index) => <SkillBadge key={index} {...skill} data-id="element-6242" />)}
                </div>
              </div>
              {/* Industry Skills */}
              <div data-id="element-6243">
                <div className="flex items-center mb-3" data-id="element-6244">
                  <ZapIcon className="w-5 h-5 text-green-600 mr-2" data-id="element-6245" />
                  <h3 className="font-medium text-gray-800" data-id="element-6246">
                    Industry-specific Skills
                  </h3>
                </div>
                <div className="flex flex-wrap gap-2" data-id="element-6247">
                  {program.skills.filter(skill => skill.category === 'Industry').map((skill, index) => <SkillBadge key={index} {...skill} data-id="element-6248" />)}
                </div>
              </div>
            </div>
            {/* AI-generated summary */}
            <div className="mt-8 p-4 bg-blue-50 border border-blue-100 rounded-lg" data-id="element-6249">
              <div className="flex items-start" data-id="element-6250">
                <SparklesIcon className="w-5 h-5 text-blue-500 mr-3 flex-shrink-0 mt-0.5" data-id="element-6251" />
                <div data-id="element-6252">
                  <h3 className="font-medium text-blue-800 mb-1" data-id="element-6253">
                    AI-Generated Insight
                  </h3>
                  <p className="text-sm text-blue-700" data-id="element-6254">
                    This program is highly aligned with UAE labor market trends.
                    The combination of{' '}
                    {program.skills.filter(s => s.category === 'Technical')[0]?.name}{' '}
                    and{' '}
                    {program.skills.filter(s => s.category === 'Soft')[0]?.name}{' '}
                    skills is in high demand across multiple industries.
                  </p>
                </div>
              </div>
            </div>
          </section>
          {/* Enrollment Form */}
          <section id="enrollment-form" className="bg-white rounded-lg border border-gray-200 shadow-sm p-8" data-id="element-6255">
            <h2 className="text-2xl font-bold text-gray-800 mb-6" data-id="element-6256">
              Enrollment Form
            </h2>
            <p className="text-gray-700 mb-6" data-id="element-6257">
              The following fields are included in the student application form:
            </p>
            <div className="overflow-hidden border border-gray-200 rounded-lg mb-6" data-id="element-6258">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-6259">
                <thead className="bg-gray-50" data-id="element-6260">
                  <tr data-id="element-6261">
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-6262">
                      Field Label
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-6263">
                      Type
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-6264">
                      Required
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-6265">
                      Eligibility Field
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-6266">
                  {program.enrollmentForm && program.enrollmentForm.map((field, index) => <tr key={index} className={field.isEligibility ? 'bg-green-50' : ''} data-id="element-6267">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-6268">
                          {field.label}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 capitalize" data-id="element-6269">
                          {field.type}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-6270">
                          {field.required ? <CheckCircleIcon className="w-5 h-5 text-green-500" data-id="element-6271" /> : <span className="text-gray-400" data-id="element-6272">Optional</span>}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-6273">
                          {field.isEligibility ? <div className="flex items-center" data-id="element-6274">
                              <CheckCircleIcon className="w-5 h-5 text-green-500 mr-1" data-id="element-6275" />
                              <span className="text-green-700 font-medium" data-id="element-6276">
                                Yes
                              </span>
                            </div> : <span className="text-gray-400" data-id="element-6277">No</span>}
                        </td>
                      </tr>)}
                </tbody>
              </table>
            </div>
            <div className="flex justify-between items-center" data-id="element-6278">
              <div className="text-sm text-gray-500" data-id="element-6279">
                <span className="inline-block w-3 h-3 bg-green-50 border border-green-100 rounded-sm mr-1" data-id="element-6280"></span>
                Highlighted rows are used for eligibility screening
              </div>
              <button className="bg-white text-primary-600 border border-primary-200 px-4 py-2 rounded-md text-sm font-medium hover:bg-primary-50 transition-colors flex items-center" data-id="element-6281">
                <ExternalLinkIcon className="w-4 h-4 mr-2" data-id="element-6282" />
                Preview Student Form
              </button>
            </div>
          </section>
          {/* Attached Documents */}
          <section id="documents" className="bg-white rounded-lg border border-gray-200 shadow-sm p-8" data-id="element-6283">
            <h2 className="text-2xl font-bold text-gray-800 mb-6" data-id="element-6284">
              Attached Documents
            </h2>
            <p className="text-gray-700 mb-6" data-id="element-6285">
              The following documents have been uploaded for this program:
            </p>
            <div className="space-y-4" data-id="element-6286">
              {program.documents && program.documents.map((doc, index) => <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-100" data-id="element-6287">
                    <div className="flex items-center" data-id="element-6288">
                      <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center mr-4" data-id="element-6289">
                        <FileTextIcon className="w-5 h-5 text-gray-600" data-id="element-6290" />
                      </div>
                      <div data-id="element-6291">
                        <p className="font-medium text-gray-800" data-id="element-6292">{doc.name}</p>
                        <p className="text-sm text-gray-500" data-id="element-6293">
                          {doc.type} • {doc.size} • Uploaded on {doc.uploaded}
                        </p>
                      </div>
                    </div>
                    <div className="flex space-x-2" data-id="element-6294">
                      <button className="p-2 text-gray-600 hover:text-gray-800 rounded-full hover:bg-gray-200" data-id="element-6295">
                        <EyeIcon className="w-5 h-5" data-id="element-6296" />
                      </button>
                      <button className="p-2 text-gray-600 hover:text-gray-800 rounded-full hover:bg-gray-200" data-id="element-6297">
                        <DownloadIcon className="w-5 h-5" data-id="element-6298" />
                      </button>
                    </div>
                  </div>)}
            </div>
          </section>
          {/* Program Structure */}
          <section id="structure" className="bg-white rounded-lg border border-gray-200 shadow-sm p-8" data-id="element-6299">
            <h2 className="text-2xl font-bold text-gray-800 mb-6" data-id="element-6300">
              Program Structure
            </h2>
            <p className="text-gray-700 mb-6" data-id="element-6301">
              The curriculum is structured to provide a comprehensive education
              with both core and elective courses:
            </p>
            <div className="space-y-6" data-id="element-6302">
              {program.programStructure.map((semester, index) => <div key={index} className="bg-gray-50 rounded-lg border border-gray-100 overflow-hidden" data-id="element-6303">
                  <div className="bg-gray-100 px-4 py-3" data-id="element-6304">
                    <h3 className="font-medium text-gray-800" data-id="element-6305">
                      {semester.semester}
                    </h3>
                  </div>
                  <div className="p-4" data-id="element-6306">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-6307">
                      {semester.courses.map((course, courseIndex) => <div key={courseIndex} className="flex items-center" data-id="element-6308">
                          <div className="w-2 h-2 rounded-full bg-primary-500 mr-2" data-id="element-6309"></div>
                          <span className="text-gray-700" data-id="element-6310">{course}</span>
                        </div>)}
                    </div>
                  </div>
                </div>)}
            </div>
          </section>
          {/* Career Opportunities */}
          {program.careerOpportunities && program.careerOpportunities.length > 0 && <section className="bg-white rounded-lg border border-gray-200 shadow-sm p-8" data-id="element-6311">
                <h2 className="text-2xl font-bold text-gray-800 mb-6" data-id="element-6312">
                  Career Opportunities
                </h2>
                <p className="text-gray-700 mb-6" data-id="element-6313">
                  Graduates of this program will be prepared for careers in
                  various roles, including:
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-6314">
                  {program.careerOpportunities.map((career, index) => <div key={index} className="flex items-center bg-gray-50 p-3 rounded-lg border border-gray-100" data-id="element-6315">
                      <TagIcon className="w-5 h-5 text-primary-500 mr-3" data-id="element-6316" />
                      <span className="text-gray-700" data-id="element-6317">{career}</span>
                    </div>)}
                </div>
              </section>}
          {/* FAQs */}
          {program.faqs && program.faqs.length > 0 && <section className="bg-white rounded-lg border border-gray-200 shadow-sm p-8" data-id="element-6318">
              <h2 className="text-2xl font-bold text-gray-800 mb-6" data-id="element-6319">
                Frequently Asked Questions
              </h2>
              <div className="space-y-1" data-id="element-6320">
                {program.faqs.map((faq, index) => <FAQItem key={index} question={faq.question} answer={faq.answer} data-id="element-6321" />)}
              </div>
            </section>}
        </div>
      </div>
    </Layout>;
};
export default ProgramDetail;